/**
 * mergeDailyStatusDigests — D9 client-side consolidation (Prompt 11E.8).
 *
 * Owner "All" mode: N per-tenant `/v1/dashboard/daily-status` responses,
 * fetched independently (see OwnerDashboard.tsx's fan-out), summed here.
 * NEVER a cross-tenant backend query — this is the entire point of D9.
 */

import type { BillingCycle, DailyStatusDigestData, RenewalCycleBucket, NewEnrolmentsCycleBucket, DropsCycleBucket, SubjectBreakdownEntry } from '@/api/dashboard';

const CYCLES: BillingCycle[] = ['monthly', 'quarterly', 'half_yearly', 'yearly', 'unknown'];

function mergeRenewalBuckets(digests: DailyStatusDigestData[]): RenewalCycleBucket[] {
  return CYCLES.map((cycle) => {
    const matching = digests.flatMap((d) => d.renewals_by_cycle.filter((b) => b.cycle === cycle));
    return {
      cycle,
      projections_count: matching.reduce((sum, b) => sum + b.projections_count, 0),
      projections_amount: matching.reduce((sum, b) => sum + b.projections_amount, 0),
      received_count: matching.reduce((sum, b) => sum + b.received_count, 0),
      received_amount: matching.reduce((sum, b) => sum + b.received_amount, 0),
    };
  });
}

function mergeEnrolmentBuckets(digests: DailyStatusDigestData[]): NewEnrolmentsCycleBucket[] {
  return CYCLES.map((cycle) => {
    const matching = digests.flatMap((d) => d.new_enrolments_by_cycle.filter((b) => b.cycle === cycle));
    return {
      cycle,
      count: matching.reduce((sum, b) => sum + b.count, 0),
      amount: matching.reduce((sum, b) => sum + b.amount, 0),
    };
  });
}

function mergeDropBuckets(digests: DailyStatusDigestData[]): DropsCycleBucket[] {
  return CYCLES.map((cycle) => {
    const matching = digests.flatMap((d) => d.drops_by_cycle.filter((b) => b.cycle === cycle));
    return { cycle, count: matching.reduce((sum, b) => sum + b.count, 0) };
  });
}

function mergeSubjectBreakdown(entries: SubjectBreakdownEntry[][]): SubjectBreakdownEntry[] {
  const bySubject = new Map<string, SubjectBreakdownEntry>();
  for (const list of entries) {
    for (const e of list) {
      const key = e.subject_id ?? e.subject_name;
      const existing = bySubject.get(key);
      if (existing) {
        existing.count += e.count;
      } else {
        bySubject.set(key, { ...e });
      }
    }
  }
  return Array.from(bySubject.values()).sort((a, b) => b.count - a.count);
}

/** Sums every numeric field across the given per-tenant digests. Returns
 * `null` for an empty list (nothing to consolidate). */
export function mergeDailyStatusDigests(digests: DailyStatusDigestData[]): DailyStatusDigestData | null {
  if (digests.length === 0) return null;
  const [first] = digests;

  return {
    tenant_id: '__all__',
    tenant_name: 'All Centres (Consolidated)',
    date: first.date,
    month_label: first.month_label,
    total_projections: digests.reduce((sum, d) => sum + d.total_projections, 0),
    total_received: digests.reduce((sum, d) => sum + d.total_received, 0),
    todays_collection: digests.reduce((sum, d) => sum + d.todays_collection, 0),
    renewals_by_cycle: mergeRenewalBuckets(digests),
    total_admissions_this_month: digests.reduce((sum, d) => sum + d.total_admissions_this_month, 0),
    todays_admissions_by_subject: mergeSubjectBreakdown(digests.map((d) => d.todays_admissions_by_subject)),
    demos_today: digests.reduce((sum, d) => sum + d.demos_today, 0),
    demos_today_by_subject: mergeSubjectBreakdown(digests.map((d) => d.demos_today_by_subject)),
    new_enrolments_by_cycle: mergeEnrolmentBuckets(digests),
    drops_by_cycle: mergeDropBuckets(digests),
    generated_at: new Date().toISOString(),
    errors: digests.flatMap((d) => d.errors),
  };
}
